<script>
  import { format } from 'svelte-i18n';
  import { Badge, Table } from '@sveltestrap/sveltestrap';
  import { createQuery } from '@tanstack/svelte-query';
  import { format as formatDate } from 'date-fns';

  import { Chart } from '@client/components/charts';
  import { Recipient } from '@client/components/recipient';

  import { ReportsService } from '@client/services/reports';

  import { appStore } from '@client/stores/app';
  import { userStore } from '@client/stores/user';

  export let domain = {};

  let reports = {
    chartLoginByUserData: [],
    chartLoginPerDayData: [],
    chartLoginByDayOfWeekData: [],
    chartLoginByHourData: [],
    chartActionData: [],
    chartActionActiveUsersData: []
  };

  const generateLoginsByUserDataset = (data) => {
    const userCounts = data.reduce((acc, entry) => {
      acc[entry.user] = (acc[entry.user] || 0) + 1;
      return acc;
    }, {});

    const sortedEntries = Object.entries(userCounts).sort((a, b) => b[1] - a[1]);

    const labels = sortedEntries.map((entry) => entry[0].split('@').shift());
    const counts = sortedEntries.map((entry) => entry[1]);

    return {
      labels,
      datasets: [
        {
          label: $format('label.LOGINS'),
          data: counts,
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }
      ]
    };
  };

  const generateChartDatasetByAction = (data) => {
    const actionCounts = data.reduce((acc, entry) => {
      acc[entry.action] = (acc[entry.action] || 0) + 1;
      return acc;
    }, {});

    const sortedEntries = Object.entries(actionCounts).sort((a, b) => b[1] - a[1]);

    const labels = sortedEntries.map((entry) => entry[0]);
    const counts = sortedEntries.map((entry) => entry[1]);

    return {
      labels,
      datasets: [
        {
          label: $format('label.EVENTS'),
          data: counts,
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }
      ]
    };
  };

  const format12HourClock = (hour) => {
    const numericHour = parseInt(hour, 10);
    const formattedHour = numericHour % 12 || 12;
    const period = numericHour >= 12 ? 'PM' : 'AM';
    return [formattedHour, period];
  };

  const generateLoginsPerDayDataset = (data) => {
    const dateCounts = data.reduce((acc, entry) => {
      const date = new Date(entry.timestamp).toISOString().split('T')[0];
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    const sortedDates = Object.keys(dateCounts).sort((a, b) => new Date(a) - new Date(b));
    const labels = sortedDates;
    const counts = sortedDates.map((date) => dateCounts[date]);

    return {
      labels,
      datasets: [
        {
          label: $format('label.LOGINS'),
          data: counts,
          fill: false,
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(75, 192, 192, 1)',
          tension: 0.1
        }
      ]
    };
  };

  const generateLoginsByDayOfWeekDataset = (data) => {
    const dayCounts = Array(7).fill(0);

    data.forEach((entry) => {
      const dayOfWeek = new Date(entry.timestamp).getDay();
      dayCounts[dayOfWeek]++;
    });

    const dayNames = [
      $format('label.DAYS_FULL.SUN'),
      $format('label.DAYS_FULL.MON'),
      $format('label.DAYS_FULL.TUE'),
      $format('label.DAYS_FULL.WED'),
      $format('label.DAYS_FULL.THU'),
      $format('label.DAYS_FULL.FRI'),
      $format('label.DAYS_FULL.SAT')
    ];

    return {
      labels: dayNames,
      datasets: [
        {
          label: $format('label.LOGINS'),
          data: dayCounts,
          backgroundColor: 'rgba(153, 102, 255, 0.5)',
          borderColor: 'rgba(153, 102, 255, 1)',
          borderWidth: 1
        }
      ]
    };
  };

  const generateLoginsByHourDataset = (data) => {
    const hourCounts = Array(24).fill(0);

    data.forEach((entry) => {
      const hour = new Date(entry.timestamp).getUTCHours();
      hourCounts[hour]++;
    });

    const labels = [...Array(24).keys()].map((hour) => {
      const formattedHour = hour % 12 || 12;
      const period = hour >= 12 ? 'PM' : 'AM';
      return `${formattedHour} ${period}`;
    });

    return {
      labels,
      datasets: [
        {
          label: 'Logins per Hour',
          data: hourCounts,
          backgroundColor: 'rgba(255, 159, 64, 0.5)',
          borderColor: 'rgba(255, 159, 64, 1)',
          borderWidth: 1
        }
      ]
    };
  };

  const generateMostActiveUsersDataset = (data) => {
    const userActionCounts = {};

    data.forEach((entry) => {
      const user = entry.user;
      userActionCounts[user] = (userActionCounts[user] || 0) + 1;
    });

    const sortedUsers = Object.entries(userActionCounts).sort((a, b) => b[1] - a[1]);
    const labels = sortedUsers.map(([user]) => user.split('@').shift());
    const counts = sortedUsers.map(([_, count]) => count);

    return {
      labels,
      datasets: [
        {
          label: $format('label.EVENTS'),
          data: counts,
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        }
      ]
    };
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      x: {
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  const optionsTimeSeries = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        ticks: {
          stepSize: 1,
          maxRotation: 0,
          minRotation: 0
        }
      },
      x: {
        type: 'time',
        time: {
          unit: 'day',
          tooltipFormat: 'MM/dd/yyyy'
        },
        ticks: {
          stepSize: 5,
          maxRotation: 0,
          minRotation: 0
        }
      }
    }
  };

  const optionsByHour = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const hour = context.label;
            const [formattedHour, period] = format12HourClock(hour);
            return `${formattedHour} ${period} - Logins: ${context.raw}`;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          callback: function (value, index, ticks) {
            return this.getLabelForValue(value);
          },
          maxRotation: 0,
          minRotation: 0,
          stepSize: 1
        }
      },
      y: {
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          stepSize: 1
        },
        beginAtZero: true
      }
    }
  };

  const query = createQuery({
    queryKey: ['edit-domain-reports'],
    queryFn: async () => {
      reports = {
        chartLoginByUserData: [],
        chartLoginPerDayData: [],
        chartLoginByDayOfWeekData: [],
        chartLoginByHourData: [],
        chartActionData: []
      };

      if ($userStore.domain === domain?.domainsettings?.domainname) {
        const response = await ReportsService.getAuditHistory({ days: 60 });
        const events = response.filter(
          ({ user }) => user?.indexOf('opensense') === -1 && user?.indexOf('allied.marketing') === -1
        );
        const loginEvents = events.filter(({ action }) => action === 'login');

        if (loginEvents.length) {
          reports.chartLoginByUserData = generateLoginsByUserDataset(loginEvents);
          reports.chartLoginPerDayData = generateLoginsPerDayDataset(loginEvents);
          reports.chartLoginByDayOfWeekData = generateLoginsByDayOfWeekDataset(loginEvents);
          reports.chartLoginByHourData = generateLoginsByHourDataset(loginEvents);
        }

        if (events.length) {
          reports.chartActionData = generateChartDatasetByAction(events);
          reports.chartActionActiveUsersData = generateMostActiveUsersDataset(events);
        }

        return {
          logins: loginEvents,
          actions: events,
          ...reports
        };
      }

      return {};
    }
  });

  $: data = $query.data || {};
</script>

{#if !$query.isFetching}
  <h3>{$format('label.REPORTS')}</h3>

  <div class="report-layout">
    <div class="report card" style="--bs-border-color-translucent: var(--border-color);">
      <div class="report-header">
        <h3>{$format('label.LOGIN')} <Badge color="danger">Last 60 Days</Badge></h3>
      </div>
      <div class="report-body">
        {#if data.logins?.length}
          <div class="login-charts-top">
            <div class="time-series">
              <h4>{$format('label.CHART_TITLES.OVER_TIME')}</h4>
              <Chart type="line" data={data.chartLoginPerDayData} options={optionsTimeSeries} />
            </div>
            <div>
              <h4>{$format('label.CHART_TITLES.BY_DAY')}</h4>
              <Chart type="bar" data={data.chartLoginByDayOfWeekData} {options} />
            </div>
            <div>
              <h4>{$format('label.CHART_TITLES.BY_HOUR')}</h4>
              <Chart type="bar" data={data.chartLoginByHourData} options={optionsByHour} />
            </div>
          </div>

          <h4>{$format('label.CHART_TITLES.BY_USER')}</h4>
          <Chart type="bar" data={data.chartLoginByUserData} {options} />

          <div class="history">
            <h4>{$format('label.HISTORY')}</h4>
            <div class="table-wrapper">
              <Table class="mt-4">
                <thead>
                  <tr>
                    <th class="fill">{$format('label.USER')}</th>
                    {#if !$appStore.mobile}
                      <th>{$format('label.TIMESTAMP')}</th>
                    {/if}
                  </tr>
                </thead>
                <tbody>
                  {#each data.logins as config}
                    <tr>
                      <td>
                        <Recipient recipient={config.user} history={[]} />
                        {#if $appStore.mobile}
                          <div class="details">
                            <b>{$format('label.LOGIN')}</b> &rarr; {formatDate(
                              new Date(config.timestamp),
                              'MM/dd/yyyy @ h:mm a'
                            )}
                          </div>
                        {/if}
                      </td>
                      {#if !$appStore.mobile}
                        <td>{formatDate(new Date(config.timestamp), 'MM/dd/yyyy @ h:mm a')}</td>
                      {/if}
                    </tr>
                  {/each}
                </tbody>
              </Table>
            </div>
          </div>
        {:else}
          {$format('label.NO_DATA_AVAILABLE')}
        {/if}
      </div>
    </div>

    <div class="report card" style="--bs-border-color-translucent: var(--border-color);">
      <div class="report-header">
        <h3>{$format('label.ACTIONS')} <Badge color="danger">Last 60 Days</Badge></h3>
      </div>
      <div class="report-body">
        {#if data.actions?.length}
          <div class="actions-charts-top">
            <div>
              <h4>{$format('label.CHART_TITLES.BY_USER')}</h4>
              <Chart type="bar" data={data.chartActionActiveUsersData} {options} />
            </div>
            <div>
              <h4>{$format('label.CHART_TITLES.BY_EVENT')}</h4>
              <Chart type="bar" data={data.chartActionData} {options} />
            </div>
          </div>
          <div class="table-wrapper">
            <Table class="mt-4">
              <thead>
                <tr>
                  <th class="fill">{$format('label.USER')}</th>
                  {#if !$appStore.mobile}
                    <th>{$format('label.ACTION')}</th>
                    <th>{$format('label.TIMESTAMP')}</th>
                  {/if}
                </tr>
              </thead>
              <tbody>
                {#each data.actions as config}
                  <tr>
                    <td>
                      <Recipient recipient={config.user} history={[]} />
                      {#if $appStore.mobile}
                        <div class="details">
                          <b>{config.action}</b> &rarr; {formatDate(new Date(config.timestamp), 'MM/dd/yyyy @ h:mm a')}
                        </div>
                      {/if}
                    </td>
                    {#if !$appStore.mobile}
                      <td>{config.action}</td>
                      <td>{formatDate(new Date(config.timestamp), 'MM/dd/yyyy @ h:mm a')}</td>
                    {/if}
                  </tr>
                {/each}
              </tbody>
            </Table>
          </div>
        {:else}
          {$format('label.NO_DATA_AVAILABLE')}
        {/if}
      </div>
    </div>
  </div>
{/if}

<style lang="scss" src="./reports.scss"></style>
